import React, {useEffect, useState} from 'react';
import {View, StyleSheet, Pressable, ScrollView} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import withIsConnected from '../../hoc/withIsConnected';
import Localized from 'src/constants/AppStrings';
import {useNavigation} from '@react-navigation/native';
import {getPreviousRouteName} from 'src/Util';
import Styles from 'src/components/Styles';
import AVText from 'src/components/elements/AVText';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import BaseScreen from 'src/components/screens/BaseScreen';
import LocationHandler from 'src/handlers/LocationHandler';
import NearbyLocationsList from 'src/components/elements/home/NearbyLocationsList';
import RecentLocationsList from 'src/components/elements/home/RecentLocationsList';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import {IsConnectedProps} from 'src/types/Screen';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {fetchNearbyCampusesAndLocations} from 'src/redux/slices/campusLocationSlice';
import {getDescriber} from './descriptor/DescriptorType';
import Settings from 'src/Settings';
import WarningDialog from 'src/components/elements/WarningDialog';

type CampusLocationsScreenProps = IsConnectedProps;
const {getCampusLocationStyles} = getDescriber();

const CampusLocationsScreen = (props: CampusLocationsScreenProps) => {
  const dispatch = useAppDispatch();
  const nearbyLocations = useAppSelector(
    (s) => s.campusLocation.nearbyCampusesAndLocations,
  );

  const navigation = useNavigation();
  const [previousRoute] = React.useState(
    getPreviousRouteName(navigation?.getState()?.routes),
  );

  const [locationMismatchError, setLocationMismatchError] = useState(false);

  useEffect(() => {
    getNearbyLocations();
    FirebaseAnalytic.trackEvent('componentDidMount', 'CampusLocationsScreen', {
      ...props,
    });
  }, []);

  //Fetches nearby locations using current location
  const getNearbyLocations = async () => {
    await LocationHandler.getCurrentLocation();
    const position = LocationHandler.currentLocation;
    dispatch(
      fetchNearbyCampusesAndLocations({
        lat: position?.coords?.latitude,
        lon: position?.coords?.longitude,
      }),
    );
  };

  const showHideLocationMismatchError = (value?: boolean) => {
    setLocationMismatchError(value ?? false);
  };

  return (
    <BaseScreen
      title={getDescriber().locationHeaderName()}
      previousRoute={previousRoute}
      accessibilityLabel={'Back arrow'}
      accessibilityHint={`Press to navigate back to the ${previousRoute} screen`}
      testId={'qa_Locations.PageTitle_txt'}
    >
      <WarningDialog
        isVisible={locationMismatchError}
        buttonAction={() => showHideLocationMismatchError()}
        closeAction={() => showHideLocationMismatchError()}
      />
      <ScrollView
        style={[
          styles.content,
          {
            backgroundColor: Settings.isRefiveAnd365Pay()
              ? Styles.tabBarBackgroundColor
              : Styles.Colors.Canteen.lightGray,
          },
        ]}
      >
        <View
          style={[
            styles.container,
            {
              backgroundColor: Settings.isRefiveAnd365Pay()
                ? Styles.tabBarBackgroundColor
                : Styles.Colors.Canteen.lightGray,
            },
          ]}
        >
          <NearbyLocationsList
            locations={nearbyLocations}
            isConnected={true}
            mismatchLocationBalance={showHideLocationMismatchError}
          />
          <RecentLocationsList
            isConnected={true}
            mismatchLocationBalance={showHideLocationMismatchError}
          />
          <View
            style={styles.buttonView}
            accessible
            testID="qa_lnk_AddLocation"
            nativeID="qa_lnk_AddLocation"
          >
            <Pressable
              accessible={true}
              accessibilityHint="Double tap to navigate to the link new location screen"
              accessibilityRole="button"
              role="button"
              accessibilityLabel={Localized.Buttons.add_location_manually}
              nativeID={Localized.Buttons.add_location_manually}
              aria-label={Localized.Buttons.add_location_manually}
              testID={Localized.Buttons.add_location_manually}
              style={[styles.buttonContainer]}
              onPress={() =>
                NavActions.navigate(AppRoutes.LocationCode, {isFromHome: true})
              }
            >
              <View style={getCampusLocationStyles().linkView}>
                <AVText
                  style={[getCampusLocationStyles().linkLocationBtnStyle]}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                >
                  {Localized.Buttons.add_location_manually}
                </AVText>
              </View>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </BaseScreen>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  buttonView: {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: Styles.Heights.h5,
    marginTop: Styles.Spacing.m1,
    marginHorizontal: Styles.Spacing.m3,
  },
  buttonContainer: {
    minHeight: Styles.MinTouchableArea,
    minWidth: Styles.MinTouchableArea,
  },
  buttonContainerMargin: {
    marginTop: Styles.Spacing.m3,
  },
  buttonText: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '700',
    color: Styles.primaryColor,
    textDecorationLine: 'underline',
    fontFamily: Settings.isRefive()
      ? Styles.FontFamily.figtreeRegular
      : Styles.FontFamily.robotoRegular,
  },
});

export default withForwardedNavigationParams()(
  withIsConnected(CampusLocationsScreen),
);
